import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  RiMailLine,
  RiPhoneLine,
  RiDeleteBinLine,
  RiRefreshLine,
  RiSearchLine,
  RiUserLine,
  RiEditLine,
  RiSendPlaneLine,
  RiAlertLine,
  RiCalendarLine,
  RiUserAddLine,
  RiBuildingLine,
  RiCheckboxCircleLine,
  RiCalendarEventLine,
  RiArrowDownLine,
  RiCheckLine,
  RiExternalLinkLine,
  RiCloseLine
} from 'react-icons/ri';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';

// ─── Event Selector ──────────────────────────────────────────────────────────
const EventSelector = ({ token, selectedEvent, onSelect }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(data.success ? data.events || [] : []);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const filtered = events.filter(
    (e) =>
      e.title?.toLowerCase().includes(search.toLowerCase()) ||
      e.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const getSlug = (ev) =>
    ev.slug ||
    ev.title
      ?.toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();

  return (
    <div style={{ position: "relative", zIndex: 100 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 20px",
          background: selectedEvent ? "#2A9D8F" : "#F4F9F8",
          border: `1px solid ${selectedEvent ? "transparent" : "#C8E8E4"}`,
          borderRadius: 12,
          cursor: "pointer",
          color: selectedEvent ? "#FFFFFF" : "#1C3461",
          fontWeight: 600,
          fontSize: 14,
          minWidth: 260,
          justifyContent: "space-between",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <RiCalendarEventLine size={16} />
          {selectedEvent ? selectedEvent.title : "Select an Event"}
        </span>
        <RiArrowDownLine
          size={14}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.2s",
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              minWidth: 340,
              background: "#FFFFFF",
              border: `1px solid #E2F2F0`,
              borderRadius: 16,
              boxShadow: "0 20px 60px rgba(13,43,62,0.20)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "12px 16px",
                borderBottom: `1px solid #E2F2F0`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <RiSearchLine size={14} style={{ color: "#6B8090" }} />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events…"
                style={{
                  border: "none",
                  outline: "none",
                  fontSize: 13,
                  color: "#1C3461",
                  background: "transparent",
                  width: "100%",
                }}
              />
            </div>

            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              {loading ? (
                <div style={{ padding: 24, textAlign: "center", color: "#6B8090", fontSize: 13 }}>
                  Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "#6B8090", fontSize: 13 }}>
                  No events found
                </div>
              ) : (
                filtered.map((ev) => {
                  const slug = getSlug(ev);
                  const isActive = selectedEvent?._id === ev._id;
                  return (
                    <button
                      key={ev._id}
                      onClick={() => {
                        onSelect({ ...ev, slug });
                        setOpen(false);
                        setSearch("");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "12px 16px",
                        background: isActive ? "#EBF1FF" : "transparent",
                        border: "none",
                        cursor: "pointer",
                        textAlign: "left",
                        borderBottom: `1px solid #E2F2F0`,
                      }}
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: isActive ? "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)" : "#F4F9F8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <RiCalendarEventLine size={16} style={{ color: isActive ? "#FFFFFF" : "#2A9D8F" }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1C3461" }}>{ev.title}</div>
                        <div style={{ fontSize: 11, color: "#6B8090" }}>/event/{slug}{ev.date && ` · ${ev.date}`}</div>
                      </div>
                      {isActive && <RiCheckLine size={14} style={{ color: "#2A9D8F" }} />}
                    </button>
                  );
                })
              )}
            </div>

            <div
              style={{
                padding: "10px 16px",
                borderTop: `1px solid #E2F2F0`,
                display: "flex",
                gap: 8,
              }}
            >
              <button
                onClick={fetchEvents}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  background: "#F4F9F8",
                  border: `1px solid #C8E8E4`,
                  borderRadius: 8,
                  fontSize: 12,
                  color: "#2D4A5A",
                  cursor: "pointer",
                }}
              >
                <RiRefreshLine size={12} /> Refresh
              </button>
              {selectedEvent && (
                <a
                  href={`/event/${selectedEvent.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    background: "#EBF1FF",
                    border: `1px solid #C8E8E4`,
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#2A9D8F",
                    textDecoration: "none",
                  }}
                >
                  <RiExternalLinkLine size={12} /> View Live
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {open && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: -1 }}
          onClick={() => setOpen(false)}
        />
      )}
    </div>
  );
};

// ─── Main ContactManagement Component ────────────────────────────────────────
const ContactManagement = () => {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: ''
  });

  // ── Fetch contacts when event changes ──────────────────────────────────────
  useEffect(() => {
    if (selectedEvent?._id) {
      fetchContacts(selectedEvent._id);
    } else {
      setContacts([]);
      setFilteredContacts([]);
      setLoading(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = contacts.filter(contact =>
        contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.phone?.includes(searchTerm) ||
        contact.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchTerm, contacts]);

  // ── Fetch contacts for a specific event ────────────────────────────────────
  const fetchContacts = async (eventId) => {
    if (!eventId) return;
    try {
      setLoading(true);
      console.log(`Fetching contacts for event: ${eventId}`);
      const response = await axios.get(`${API_BASE}/api/contacts/event/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Contacts response:', response.data);
      if (response.data.success) {
        const contactsData = response.data.data || [];
        setContacts(contactsData);
        setFilteredContacts(contactsData);
      } else {
        setContacts([]);
        setFilteredContacts([]);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error(error.response?.data?.message || 'Failed to load contacts');
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Save new contact ──────────────────────────────────────────────────────
  const saveContact = async () => {
    if (!selectedEvent?._id) {
      toast.error('Please select an event first');
      return;
    }
    if (!formData.email && !formData.phone) {
      toast.error('Please enter at least email or phone number');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/contacts`,
        {
          eventId: selectedEvent._id,
          email: formData.email,
          phone: formData.phone,
          name: formData.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Contact saved successfully!');
        setFormData({ email: '', phone: '', name: '' });
        await fetchContacts(selectedEvent._id);
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  // ── Update existing contact ──────────────────────────────────────────────
  const updateContact = async () => {
    if (!selectedEvent?._id) {
      toast.error('Please select an event first');
      return;
    }
    if (!formData.email && !formData.phone) {
      toast.error('Please enter at least email or phone number');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE}/api/contacts/${editingContact._id}`,
        {
          eventId: selectedEvent._id,
          email: formData.email,
          phone: formData.phone,
          name: formData.name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Contact updated successfully!');
        setFormData({ email: '', phone: '', name: '' });
        setEditingContact(null);
        await fetchContacts(selectedEvent._id);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete contact ────────────────────────────────────────────────────────
  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    if (!selectedEvent?._id) {
      toast.error('Please select an event first');
      return;
    }
    setSaving(true);
    try {
      const response = await axios.delete(
        `${API_BASE}/api/contacts/${contactId}?eventId=${selectedEvent._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Contact deleted successfully!');
        await fetchContacts(selectedEvent._id);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const editContact = (contact) => {
    setEditingContact(contact);
    setFormData({
      email: contact.email || '',
      phone: contact.phone || '',
      name: contact.name || ''
    });
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingContact(null);
    setFormData({ email: '', phone: '', name: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  // ─── No event selected ─────────────────────────────────────────────────────
  if (!selectedEvent) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Contact Management</h2>
          <EventSelector token={token} selectedEvent={selectedEvent} onSelect={setSelectedEvent} />
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <RiAlertLine size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900">Select an Event</h3>
          <p className="text-gray-500 mt-2">Please select an event to manage its contacts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Event Selector */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Management</h2>
          <p className="text-gray-600 mt-1">Manage contacts for <strong>{selectedEvent.title}</strong></p>
        </div>
        <EventSelector token={token} selectedEvent={selectedEvent} onSelect={setSelectedEvent} />
      </div>

      {/* Search Bar */}
      <div className="relative">
        <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by email, phone, or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <RiUserAddLine className="text-blue-600" size={20} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With Email</p>
              <p className="text-2xl font-bold text-gray-900">
                {contacts.filter(c => c.email && c.email.trim()).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <RiMailLine className="text-green-600" size={20} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With Phone</p>
              <p className="text-2xl font-bold text-gray-900">
                {contacts.filter(c => c.phone && c.phone.trim()).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <RiPhoneLine className="text-purple-600" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Contact Form */}
      <div id="contact-form" className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {editingContact ? 'Edit Contact' : 'Create New Contact'}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <RiUserLine className="inline mr-1" size={14} /> Name (Optional)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <RiMailLine className="inline mr-1" size={14} /> Email Address
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="Enter email address"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <RiPhoneLine className="inline mr-1" size={14} /> Phone Number
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter phone number (e.g., +1234567890)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={editingContact ? updateContact : saveContact}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                {editingContact ? 'Updating...' : 'Saving...'}
              </>
            ) : (
              <>
                <RiSendPlaneLine size={16} />
                {editingContact ? 'Update Contact' : 'Save Contact'}
              </>
            )}
          </button>
          {editingContact && (
            <button
              onClick={cancelEdit}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Saved Contacts List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Saved Contacts ({filteredContacts.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <RiAlertLine size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-500">No contacts for this event yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first contact using the form above</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredContacts.map((contact, index) => (
              <motion.div
                key={contact._id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                        {contact.name ? contact.name.charAt(0).toUpperCase() : contact.email ? contact.email.charAt(0).toUpperCase() : 'C'}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {contact.name || 'Unnamed Contact'}
                        </h4>
                        {contact.name && (
                          <p className="text-xs text-gray-500">ID: {contact._id?.slice(-6) || 'N/A'}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                      {contact.email && contact.email.trim() && (
                        <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <RiMailLine className="text-green-600" size={16} />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="text-sm font-medium text-gray-900 break-all">{contact.email}</p>
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(contact.email);
                              toast.success('Email copied!');
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Copy
                          </button>
                        </div>
                      )}

                      {contact.phone && contact.phone.trim() && (
                        <div className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                            <RiPhoneLine className="text-purple-600" size={16} />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Phone</p>
                            <p className="text-sm font-medium text-gray-900">{contact.phone}</p>
                          </div>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(contact.phone);
                              toast.success('Phone number copied!');
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Copy
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-3 ml-1">
                      <RiCalendarLine className="text-gray-400" size={14} />
                      <p className="text-xs text-gray-500">
                        Created: {formatDate(contact.createdAt)}
                      </p>
                      {contact.updatedAt && contact.updatedAt !== contact.createdAt && (
                        <>
                          <span className="text-gray-300">•</span>
                          <RiCheckboxCircleLine className="text-green-500" size={14} />
                          <p className="text-xs text-gray-500">
                            Updated: {formatDate(contact.updatedAt)}
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => editContact(contact)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Edit Contact"
                    >
                      <RiEditLine size={18} />
                    </button>
                    <button
                      onClick={() => deleteContact(contact._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete Contact"
                    >
                      <RiDeleteBinLine size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactManagement;